import React from "react";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAllCardTypes, deleteCardType } from "@/features/cards/api/cardsApi";
import { CardType } from "@/features/cards/types";
import { createCardTypeColumns } from "@/features/cards/components/cardTypeColumns";
import AddCardTypeDialog from "@/features/cards/components/AddCardTypeDialog";
import EditCardTypeDialog from "@/features/cards/components/EditCardTypeDialog";

export default function CardTypesPage() {
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingType, setEditingType] = React.useState<CardType | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const confirmActionRef = React.useRef<() => void>(() => {});

  const { data: cardTypes = [], isLoading } = useQuery<CardType[]>({
    queryKey: ["card-types"],
    queryFn: fetchAllCardTypes,
    staleTime: 1000 * 60 * 5,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCardType(id),
    onMutate: () => toast.loading("Deleting card type..."),
    onSuccess: () => {
      toast.dismiss();
      toast.success("Card type deleted");
      queryClient.invalidateQueries({ queryKey: ["card-types"] });
      setConfirmOpen(false);
    },
    onError: () => {
      toast.dismiss();
      toast.error("Failed to delete card type");
      setConfirmOpen(false);
    },
  });

  const handleEdit = React.useCallback((cardType: CardType) => {
    setEditingType(cardType);
    setEditOpen(true);
  }, []);

  const handleDelete = React.useCallback(
    (cardType: CardType) => {
      confirmActionRef.current = () => {
        setConfirmOpen(false);
        deleteMutation.mutate(cardType.id);
      };
      setConfirmOpen(true);
    },
    [deleteMutation]
  );

  const columns = React.useMemo(
    () => createCardTypeColumns({ onEdit: handleEdit, onDelete: handleDelete }),
    [handleEdit, handleDelete]
  );

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">Card Types</h1>
      <p className="text-muted-foreground">Manage all card type categories.</p>

      <div className="flex justify-start">
        <Button size="sm" onClick={() => setAddOpen(true)} className="flex items-center gap-2">
          <Plus size={16} /> Add Card Type
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="animate-spin w-6 h-6" />
        </div>
      ) : (
        <DataTable columns={columns} data={cardTypes} placeholder="Filter card types" filterColumns={["type"]} />
      )}

      <AddCardTypeDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ["card-types"] })}
      />

      <EditCardTypeDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        cardType={editingType}
        onSaved={() => queryClient.invalidateQueries({ queryKey: ["card-types"] })}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        actionButtonRef={confirmActionRef}
        isLoading={deleteMutation.isPending}
        AlertdialogDescription="Are you sure you want to delete this card type? This action cannot be undone."
      />
    </div>
  );
}
